import { useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { Link, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import PhoneIcon from '@mui/icons-material/PhoneOutlined'
import EmailIcon from '@mui/icons-material/EmailOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import api from 'services/api'
import CopyToClipboard from 'components/core/CopyToClipboard'
import { Stamp } from 'components/core/Clock'
import { Section, Line } from '../Moments/MomentsSplit'
import { usePlayer } from '../Moments/MomentsBrowser'
import ReportCard, { PlayerAudio } from '../Reports/ReportCard'
import { ago } from '../Staff/MicRail'
import ActivityChart from './ActivityChart'
import ActivityStrips from './ActivityStrips'
import UserDay from './UserDay'
import {
  MicChip,
  displayName,
  fmtStreamed,
  plural,
  stopHelp,
  stopLabel,
  useSeriesColors,
} from './vocabulary'

// One user, opened beside the list: who they are and how to reach them (the
// Chat page's profile, with its Team Reclip thread a click away), where to
// read their moments and their feed, their microphone off the heartbeat lease,
// and their last 30 days: totals, one chart of them all (each measure on its
// own row behind a fold), and any day opened at the minute. The server keeps
// 30 days of history for this page for now, and the pane says so.
//
// Last, every report about them or what they said (owner, 2026-11-04): a
// person report, or one of their quotes or comments, any status. While any is
// open a banner under their name says so. A report opens on its own page,
// where the actions are; nothing here acts on one.

// The user's day facts as the rows draw them, each in its measure's color
// (`useSeriesColors`); the pushed are a part of the moments.
const SERIES = [
  {
    key: 'streamed_ms',
    label: 'Streamed',
    color: 'streamed',
    format: fmtStreamed,
  },
  {
    key: 'quotes',
    label: 'Moments got',
    color: 'moments',
    part: { key: 'pushed', label: 'pushed' },
    format: (n) => plural(n, 'moment'),
  },
  {
    key: 'comments',
    label: 'Comments made',
    color: 'comments',
    format: (n) => plural(n, 'comment'),
  },
  {
    key: 'friends_added',
    label: 'Friends added',
    color: 'friends',
    format: (n) => plural(n, 'friend'),
  },
]

// The day-by-day table's columns: the measures, the pushed beside the moments.
const COLUMNS = [
  { key: 'streamed_ms', label: 'Streamed' },
  { key: 'quotes', label: 'Moments got' },
  { key: 'pushed', label: 'Pushed' },
  { key: 'comments', label: 'Comments made' },
  { key: 'friends_added', label: 'Friends added' },
]

// The Moments page opens on one account (`user`, `name` for the filter's
// label) and one of its lists (`view`).
const momentsPath = (user, view) => {
  const params = new URLSearchParams({ user: user.user_id })
  if (user.username) params.set('name', user.username)
  if (view === 'feed') params.set('view', 'feed')
  return `/moments?${params}`
}

// The Overview opens on one account the same way, and its links carry it on
// to the Pipeline page, the Loop page and each panel.
const overviewPath = (user) => {
  const params = new URLSearchParams({ user: user.user_id })
  if (user.username) params.set('name', user.username)
  return `/overview?${params}`
}

const age = (dateOfBirth) =>
  dateOfBirth ? dayjs().diff(dayjs(dateOfBirth), 'year') : null

const ContactRow = ({ Icon, value, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Icon sx={{ fontSize: '1.1em', color: 'text.secondary' }} />
    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
    <CopyToClipboard data={value} title={title} />
  </Box>
)

// The Chat page opens the thread named by ?cid= (its `DeepLink`).
const chatPath = (cid) => `/chat?cid=${encodeURIComponent(cid)}`

// Their Team Reclip thread on the Chat page, in a new tab so this page keeps
// its place. A guest, or an account never onboarded, has none.
const TeamReclipChat = ({ cid }) => {
  const button = (
    <Button
      size="small"
      variant="contained"
      disableElevation
      startIcon={<ChatBubbleOutlineIcon />}
      disabled={!cid}
      {...(cid
        ? {
            component: Link,
            to: chatPath(cid),
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {})}
      sx={{ textTransform: 'none' }}
    >
      TR Chat
    </Button>
  )
  if (cid) return button
  return (
    <Tooltip title="No Team Reclip thread: a guest, or an account never onboarded">
      <span>{button}</span>
    </Tooltip>
  )
}

const Header = ({ user }) => {
  const years = age(user.date_of_birth)
  const facts = [
    user.username ? `@${user.username}` : 'no username',
    user.is_guest && 'guest',
    user.is_staff && 'staff',
    years != null && `${years} years old`,
    user.created_at && `joined ${dayjs(user.created_at).format('M/D/YY')}`,
    user.timezone_id,
  ].filter(Boolean)
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Avatar
        src={user.picture || undefined}
        alt={displayName(user)}
        sx={{ width: 80, height: 80, fontSize: 32 }}
      >
        {(user.full_name || user.username || '?')[0].toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" fontWeight={700} noWrap>
          {user.full_name || 'No name'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {facts.join(' · ')}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TeamReclipChat cid={user.team_reclip_cid} />
        </Box>
        <Box sx={{ mt: 0.75 }}>
          {user.phone_number && (
            <ContactRow
              Icon={PhoneIcon}
              value={user.phone_number}
              title="Copy phone number"
            />
          )}
          {user.email && (
            <ContactRow
              Icon={EmailIcon}
              value={user.email}
              title="Copy email"
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Whose clock the days are on: theirs, named, or UTC when the account has no
// timezone the server can read.
const clockNote = (user, timezone) =>
  timezone === 'UTC' && user.timezone_id !== 'UTC'
    ? 'in UTC (the account has no timezone the server can read)'
    : `on their clock (${timezone})`

const Heading = ({ children, note }) => (
  <Box sx={{ mt: 2.5, mb: 1 }}>
    <Typography variant="subtitle2">{children}</Typography>
    {note && <Line>{note}</Line>}
  </Box>
)

// The microphone as the staff rail reads it, and how the latest stop was
// named. The reason outlives a resume, so while the mic is on it is history.
const Microphone = ({ presence, zone }) => {
  if (!presence.last_heartbeat_at)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MicChip presence={presence} />
        <Line>This account has never sent a heartbeat.</Line>
      </Box>
    )
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MicChip presence={presence} />
        <Line>
          last heartbeat {ago(presence.beat_age_s)},{' '}
          <Stamp ms={Date.parse(presence.last_heartbeat_at)} zone={zone} />
        </Line>
      </Box>
      {presence.last_stop_reason && (
        <Line>
          {presence.recording ? 'Last stopped' : 'Stopped'}:{' '}
          <b>{stopLabel(presence.last_stop_reason)}</b>,{' '}
          {ago(presence.last_stop_age_s)}. {stopHelp(presence.last_stop_reason)}
        </Line>
      )}
      {!presence.last_stop_reason && !presence.recording && (
        <Line>The phone did not say why it stopped.</Line>
      )}
    </Box>
  )
}

const Tile = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
      {value}
    </Typography>
    {detail && <Line>{detail}</Line>}
  </Paper>
)

// Where each of the window's moments stands now. The partition always sums to
// the total; withheld and removed are said only when there are any.
const standings = (counts) =>
  [
    `${counts.approved} approved`,
    `${counts.discarded} discarded`,
    `${counts.pending} waiting`,
    counts.withheld && `${counts.withheld} withheld (media)`,
    counts.removed && `${counts.removed} removed`,
  ]
    .filter(Boolean)
    .join(' · ')

const Stats = ({ stats }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: 1,
    }}
  >
    <Tile label="Streamed" value={fmtStreamed(stats.streamed_ms)} />
    <Tile
      label="Moments got"
      value={stats.quotes.total}
      detail={standings(stats.quotes)}
    />
    <Tile
      label="Moment pushes"
      value={stats.quotes.pushed}
      detail={`${stats.quotes.pulled} pulled by their phone`}
    />
    <Tile label="Comments made" value={stats.comments} />
    <Tile
      label="Friends"
      value={stats.friends.now}
      detail={`${stats.friends.added} added`}
    />
  </Box>
)

// The overview's key: each measure's color, the mark it draws (a line for the
// timeline, a block for a bar), and the hint that a day opens.
const Swatch = ({ color, label, line }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      sx={{
        width: line ? 14 : 10,
        height: line ? 2 : 10,
        bgcolor: color,
        borderRadius: line ? 1 : '2px',
      }}
    />
    <span>{label}</span>
  </Box>
)

const OverviewLegend = ({ colors }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1.5,
      rowGap: 0.5,
      fontSize: 12,
      color: 'text.secondary',
    }}
  >
    <Swatch line color={colors.streamed} label="Streamed" />
    <Swatch color={colors.moments} label="Moments pushed" />
    <Swatch color={colors.unpushed} label="Moments not pushed" />
    <Swatch color={colors.comments} label="Comments made" />
    <Swatch color={colors.friends} label="Friends added" />
    <Box component="span" sx={{ ml: 'auto', fontStyle: 'italic' }}>
      Click a day to open it
    </Box>
  </Box>
)

// The strips' values as rows, for reading without hovering: the active days,
// newest first.
const DayTable = ({ days }) => {
  const active = days
    .filter((day) => COLUMNS.some(({ key }) => day[key]))
    .reverse()
  if (!active.length) return <Line>No activity in the window.</Line>
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Day</TableCell>
          {COLUMNS.map(({ key, label }) => (
            <TableCell key={key} align="right">
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {active.map((day) => (
          <TableRow key={day.day}>
            <TableCell>{dayjs(day.day).format('ddd MMM D')}</TableCell>
            {COLUMNS.map(({ key }) => (
              <TableCell key={key} align="right">
                {key === 'streamed_ms'
                  ? fmtStreamed(day[key])
                  : day[key] || '·'}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// Every report about the person or what they said, read with the profile and
// again on each of the page's refreshes.
const useReportsAbout = (userId, refreshes) => {
  const [about, setAbout] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    let live = true
    api.reports
      .listForUser({ userId })
      .then((data) => {
        if (!live) return
        setAbout(data)
        setError(null)
      })
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [userId, refreshes])
  return { about, error }
}

// Under their name while any report about them is open: how many, and the way
// to them.
const ReportsBanner = ({ about, onShow }) =>
  about?.open > 0 ? (
    <Alert
      severity="warning"
      sx={{ mt: 1.5 }}
      action={
        <Button color="inherit" size="small" onClick={onShow}>
          See them
        </Button>
      }
    >
      {plural(about.open, 'open report')} about this account or what it said.
    </Alert>
  ) : null

// The reports, newest first, each as the Reports page shows it, without its
// actions: a click opens its own page, where they are.
const ReportsAbout = ({ reports, player }) => {
  const navigate = useNavigate()
  if (reports.error) return <Line>{reports.error}</Line>
  if (!reports.about) return <CircularProgress size={14} />
  const { about } = reports
  if (!about.reports.length)
    return <Line>Nobody has reported them or anything they said.</Line>
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {about.reports.map((report) => (
        <ReportCard
          key={report.id}
          report={report}
          player={player}
          onOpen={() => navigate(`/reports/${report.id}`)}
          showOpen
        />
      ))}
      {about.more && (
        <Line>The newest {about.reports.length}; there are more.</Line>
      )}
    </Box>
  )
}

const reportsLine = ({ about, error }) => {
  if (error) return 'could not be read'
  if (!about) return 'reading…'
  if (!about.reports.length) return 'none'
  return `${plural(about.reports.length, 'report')}${about.more ? '+' : ''} · ${
    about.open
  } open`
}

// Read when the user is opened and again on each of the page's refreshes
// (`refreshes` counts them); a refresh keeps what is on screen until its read
// lands. The pane is keyed by the user, so another pick mounts it afresh.
const UserDetail = ({ userId, refreshes = 0 }) => {
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState(null)
  const [reading, setReading] = useState(false)
  const [tableOpen, setTableOpen] = useState(false)
  const [rowsOpen, setRowsOpen] = useState(false)
  // The day opened at the minute; a second click on it closes it.
  const [openDay, setOpenDay] = useState(null)
  const pickDay = (day) =>
    setOpenDay((current) => (current === day ? null : day))
  const colors = useSeriesColors()
  const reports = useReportsAbout(userId, refreshes)
  // The reports fold starts open while any report is open, until it is
  // toggled; the banner's button opens it and scrolls to it.
  const [reportsToggled, setReportsToggled] = useState(null)
  const reportsOpen = reportsToggled ?? reports.about?.open > 0
  const reportsRef = useRef(null)
  const showReports = () => {
    setReportsToggled(true)
    reportsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const player = usePlayer()

  useEffect(() => {
    let live = true
    setError(null)
    setReading(true)
    api.streamMetrics
      .getUser({ userId })
      .then((data) => live && setDetail(data))
      .catch((err) => live && setError(err.message))
      .finally(() => live && setReading(false))
    return () => {
      live = false
    }
  }, [userId, refreshes])

  if (!detail)
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <CircularProgress size={20} />
        )}
      </Paper>
    )

  const { user, presence, stats, days } = detail
  const activeDays = days.filter((day) =>
    COLUMNS.some(({ key }) => day[key])
  ).length
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The refresh failed, so this is the earlier read: {error}
        </Alert>
      )}
      <Header user={user} />
      <ReportsBanner about={reports.about} onShow={showReports} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={momentsPath(user, 'moments')}
          sx={{ textTransform: 'none' }}
        >
          Their moments
        </Button>
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={momentsPath(user, 'feed')}
          sx={{ textTransform: 'none' }}
        >
          Their feed, with friends’ moments
        </Button>
        <Button
          size="small"
          component={Link}
          to={overviewPath(user)}
          sx={{ textTransform: 'none' }}
        >
          Their pipeline overview
        </Button>
      </Box>

      <Heading
        note={
          reading
            ? 'reading…'
            : `as of ${dayjs(detail.as_of).format('HH:mm:ss')}`
        }
      >
        Microphone
      </Heading>
      <Microphone presence={presence} zone={detail.timezone} />

      <Heading
        note={`The last ${detail.history_days} days, ${clockNote(
          user,
          detail.timezone
        )}: the history this page keeps for now.`}
      >
        Last {detail.history_days} days
      </Heading>
      <Stats stats={stats} />
      <Box sx={{ mt: 2 }}>
        <OverviewLegend colors={colors} />
        <ActivityChart
          days={days}
          colors={colors}
          selectedDay={openDay}
          timezone={detail.timezone}
          onSelectDay={pickDay}
        />
      </Box>
      {openDay && (
        <UserDay
          userId={user.user_id}
          username={user.username}
          day={openDay}
          days={days}
          timezone={detail.timezone}
          refreshes={refreshes}
          onDay={setOpenDay}
        />
      )}
      <Box sx={{ mt: 2 }}>
        <Section
          title="Each on its own row"
          summary={<Line>streamed, moments, comments, friends</Line>}
          open={rowsOpen}
          onToggle={() => setRowsOpen((open) => !open)}
        >
          <ActivityStrips
            days={days}
            series={SERIES}
            colors={colors}
            selectedDay={openDay}
            onSelectDay={pickDay}
          />
        </Section>
        <Section
          title="Day by day"
          summary={<Line>{plural(activeDays, 'active day')}</Line>}
          open={tableOpen}
          onToggle={() => setTableOpen((open) => !open)}
        >
          <DayTable days={days} />
        </Section>
        <Box ref={reportsRef}>
          <Section
            title="Reports"
            summary={<Line>{reportsLine(reports)}</Line>}
            open={reportsOpen}
            onToggle={() => setReportsToggled(!reportsOpen)}
          >
            <ReportsAbout reports={reports} player={player} />
          </Section>
        </Box>
      </Box>
      <PlayerAudio player={player} />
    </Paper>
  )
}

export default UserDetail
